/**
 * useClaude — Auger-agent streaming with reactive state and cancellation.
 *
 * Every caller passes an agent prompt; stream() automatically prefixes the
 * Auger system prompt + the generated domain markdown summary so every
 * agent sees the same baseline. Each caller gets its own
 * { isStreaming, error, stream, cancel } tuple.
 */

import { ref } from "vue"
import type { SchemaField } from "@auger/core"
import { schemasToMarkdown, typesToMarkdown } from "@/common/catalogMarkdown"
import {
  type ChatMessage as ClaudeMessage,
  type NavigateAction,
  streamMessage,
  type StreamOptions as ClaudeStreamOptions,
} from "@/common/claude"
import type { FetchDetailedRecordBySlugQuery } from "@/gql/graphql"
import { buildPrompt } from "@/stores/prompts"
import { getAllSchemas, getSchema } from "@/stores/schemas"
import { getAllTypes } from "@/stores/types"

const SYSTEM_PROMPT_SLUG = "ai-system-prompt"

/**
 * Build the Auger system prefix — the baseline every agent sits on top of.
 * Exposed for non-composable callers (e.g. service-layer flows) that need
 * to assemble a prompt without going through useClaude.stream.
 */
export function buildSystemPrefix(): Promise<string> {
  return buildPrompt(SYSTEM_PROMPT_SLUG, {
    types: typesToMarkdown(getAllTypes()),
    schemas: schemasToMarkdown(getAllSchemas()),
  })
}

export type { NavigateAction }

export interface ChatMessage extends ClaudeMessage {
  /** Optional client-side timestamp (ms). Stripped at the wire boundary. */
  timestamp?: number
}

type StreamOptions = Pick<ClaudeStreamOptions, "onNavigate">

export function useClaude() {
  const isStreaming = ref(false)
  const error = ref<string | null>(null)
  let abortController: AbortController | null = null

  function cancel() {
    if (abortController) {
      abortController.abort()
      abortController = null
    }
    isStreaming.value = false
  }

  function stream(
    agentPrompt: string,
    messages: ChatMessage[],
    onDelta: (text: string) => void,
    options: StreamOptions = {}
  ): Promise<void> {
    error.value = null
    // Streaming from the start: the first prompt build may load the prompts.
    isStreaming.value = true

    // Missing prompt records return empty strings (with a toast surfaced by
    // the prompts store). Bail out cleanly rather than sending a malformed
    // request to Claude.
    return buildSystemPrefix()
      .then((systemPrefix) => {
        if (!agentPrompt || !systemPrefix) return

        abortController = new AbortController()

        const system = systemPrefix + "\n\n" + agentPrompt

        return streamMessage(system, messages, onDelta, {
          signal: abortController.signal,
          onNavigate: options.onNavigate,
        })
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") return
        error.value = err.message || "Claude request failed"
        throw err
      })
      .finally(() => {
        isStreaming.value = false
        abortController = null
      })
  }

  return { isStreaming, error, stream, cancel }
}

// ── Record context formatting ──────────────────────────────────────
// A record-shaped blob of fields / links / contacts / tags / times,
// rendered as markdown for use as AI context.

export interface RecordContext {
  id: number
  type: string
  name: string
  slug: string
  description?: string | null
  data?: Record<string, unknown> | null
  schema?: { name: string; fields: Array<Pick<SchemaField, "key" | "label" | "type">> } | null
  links?: Array<{
    linked: { id: number; type: string; name: string; slug: string }
    schema_key?: string
  }>
  backlinks?: Array<{
    record: { id: number; type: string; name: string; slug: string }
    schema_key?: string
  }>
  listings?: Array<{
    list: {
      id: number
      type: string
      name: string
      slug: string
      parent?: { id: number; type: string; name: string; slug: string } | null
    }
    label?: string | null
  }>
  contacts?: Array<{ contact_type: string; value: string; label?: string | null }>
  tags?: Array<{ name: string }>
  times?: Array<{
    time_type: string
    title?: string | null
    start_at?: string
    end_at?: string | null
  }>
  owner?: { name: string } | null
}

type DetailedRecord = FetchDetailedRecordBySlugQuery["records"][number]

/** A detailed record, or a loaded one that lacks the owner, backlinks and listings. */
type ContextSource = Pick<
  DetailedRecord,
  "id" | "type" | "name" | "slug" | "description" | "data" | "schema_id"
> &
  Pick<DetailedRecord, "links" | "contacts" | "taggings" | "times"> &
  Partial<Pick<DetailedRecord, "owner" | "backlinks" | "listings">>

/**
 * Map a loaded/detailed record into the RecordContext shape recordToMarkdown
 * consumes. Shared by useChat (the sidebar) and blocks that feed record context
 * to Claude (e.g. PromptBlock), so the mapping lives in one place.
 */
export function mapRecordForContext(rec: ContextSource): RecordContext {
  const schema = getSchema(rec.schema_id)
  return {
    id: rec.id,
    type: rec.type,
    name: rec.name,
    slug: rec.slug,
    description: rec.description,
    data: rec.data,
    owner: rec.owner,
    schema: schema ? { name: schema.name, fields: schema.fields || [] } : null,
    links: rec.links?.map((l) => ({ schema_key: l.schema_key, linked: l.linked })),
    backlinks: rec.backlinks?.map((b) => ({ schema_key: b.schema_key, record: b.record })),
    listings: rec.listings?.map((l) => ({ label: l.label, list: l.list })),
    contacts: rec.contacts,
    tags: rec.taggings?.map((t) => ({ name: t.tag.name })),
    times: rec.times,
  }
}

export function recordToMarkdown(record: RecordContext): string {
  const lines: string[] = []

  lines.push(`# ${record.name}`)
  lines.push(`Record ID: ${record.id}`)
  lines.push(`Type: ${record.type}`)
  lines.push(`Slug: ${record.slug}`)
  if (record.description) lines.push(`Description: ${record.description}`)
  if (record.owner?.name) lines.push(`Owner: ${record.owner.name}`)

  if (record.schema?.fields && record.data) {
    lines.push("")
    lines.push("## Fields")
    for (const field of record.schema.fields) {
      const value = record.data[field.key]
      if (value != null && value !== "") {
        lines.push(`${field.label}: ${value}`)
      }
    }
  }

  if (record.links?.length) {
    lines.push("")
    lines.push("## Linked Records")
    for (const link of record.links) {
      const label = link.schema_key ? `${link.schema_key}: ` : ""
      lines.push(`- ${label}${link.linked.name} (${link.linked.type})`)
    }
  }

  if (record.backlinks?.length) {
    lines.push("")
    lines.push("## Backlinks (records that reference this one)")
    for (const back of record.backlinks) {
      const label = back.schema_key ? `${back.schema_key}: ` : ""
      lines.push(`- ${label}${back.record.name} (${back.record.type})`)
    }
  }

  if (record.listings?.length) {
    lines.push("")
    lines.push("## Appears in Lists")
    for (const listing of record.listings) {
      const parent = listing.list.parent
      const parentSuffix = parent ? ` (of ${parent.name} — ${parent.type})` : ""
      const label = listing.label ? ` — ${listing.label}` : ""
      lines.push(`- ${listing.list.name}${parentSuffix}${label}`)
    }
  }

  if (record.contacts?.length) {
    lines.push("")
    lines.push("## Contacts")
    for (const contact of record.contacts) {
      const label = contact.label ? ` (${contact.label})` : ""
      lines.push(`- ${contact.contact_type}: ${contact.value}${label}`)
    }
  }

  if (record.tags?.length) {
    lines.push("")
    lines.push("## Tags")
    lines.push(record.tags.map((t) => t.name).join(", "))
  }

  if (record.times?.length) {
    lines.push("")
    lines.push("## Recent Dates/Events")
    for (const time of record.times.slice(0, 10)) {
      const title = time.title || time.time_type
      const date = time.start_at ? new Date(time.start_at).toLocaleDateString() : ""
      lines.push(`- ${title}: ${date}`)
    }
  }

  return lines.join("\n")
}
